import React, { useEffect, useRef, useState } from "react";

/** Smoothly animates the displayed number to `value` whenever it changes.
 *  rAF-based (no external animation lib) so it's robust under StrictMode. */
export default function CountUp({ value = 0, duration = 1000, className = "" }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = Number(value) || 0;
    if (from === to) {
      setDisplay(to);
      return;
    }
    const dur = typeof duration === "number" && duration > 5 ? duration : 1000;
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    let start = null;

    const tick = (now) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / dur);
      setDisplay(Math.round(from + (to - from) * ease(t)));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    // rAF pauses in a background tab; make sure the final value lands regardless.
    const settle = setTimeout(() => { setDisplay(to); fromRef.current = to; }, dur + 100);

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(settle);
      fromRef.current = to; // ensure next run starts from the intended target
    };
  }, [value, duration]);

  return <span className={className}>{display}</span>;
}
